import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import RetailerPriceModeFilters from "./RetailerPriceModeFilters";
import {
  fetchActowizFeatures,
  fetchActowizProducts,
  fetchActowizTopDeals,
  getFeaturesRequestKey,
} from "../store/actowiz/actowizSlice";
import {
  getCatalogFilterModes,
  normalizeStoreKey,
  PLATFORM_LABELS,
  RETAIL_KEYS,
} from "../utils/catalogFilters";

const QUICK_OFFSET = 12;

function quickCompareRequestKey({ retailer, priceMode }) {
  const o = QUICK_OFFSET;
  const isOfferMode = priceMode === "offer_loyalty";
  if (retailer === "all" && isOfferMode) {
    return getFeaturesRequestKey({ offer: true, start: 0, offset: o });
  }
  if (isOfferMode) {
    const platKey = retailer === "all" ? "" : retailer;
    return `top_deals:${encodeURIComponent("")}:${encodeURIComponent(platKey)}:${encodeURIComponent(
      ""
    )}:${encodeURIComponent("")}:${encodeURIComponent("")}:0:${o}`;
  }
  if (retailer === "all") {
    return getFeaturesRequestKey({ start: 0, offset: o });
  }
  return `products:${encodeURIComponent("")}:${retailer}:${encodeURIComponent("")}:${encodeURIComponent("")}:0:${o}`;
}

const SEE_ALL_BUTTON_CLASS =
  "rounded-full bg-[#7352C7] px-8 py-2 text-[15px] font-bold text-white shadow-sm hover:bg-[#6546c3] sm:py-1.5 sm:text-[16px]";

export default function HomeQuickCompareSection({
  title = "Quick Compare",
  onAddToList,
  onSeeAll,
  onOpenProduct,
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [retailer, setRetailer] = useState("all");
  const [priceMode, setPriceMode] = useState("base");

  const features = useAppSelector((s) => s.actowiz.features);
  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const requests = useAppSelector((s) => s.actowiz.requests);
  const {
    isOfferMode,
    isFeaturesCompareMode,
    isFeaturesOfferCompareMode,
    isPlatformOfferMode,
  } = getCatalogFilterModes(retailer, priceMode);
  const isMultiRetailerCompareMode = isFeaturesCompareMode || isFeaturesOfferCompareMode;

  useEffect(() => {
    const arg = { start: 0, offset: QUICK_OFFSET, mode: "replace", force: true };
    if (isFeaturesOfferCompareMode) {
      dispatch(fetchActowizFeatures({ ...arg, offer: true }));
      return;
    }
    if (isPlatformOfferMode) {
      dispatch(
        fetchActowizTopDeals({
          ...arg,
          platform_list: [retailer],
        })
      );
      return;
    }
    if (isFeaturesCompareMode) {
      dispatch(fetchActowizFeatures(arg));
      return;
    }
    dispatch(fetchActowizProducts({ ...arg, platform: retailer }));
  }, [
    retailer,
    priceMode,
    isFeaturesOfferCompareMode,
    isPlatformOfferMode,
    isFeaturesCompareMode,
    dispatch,
  ]);

  const reqKey = quickCompareRequestKey({ retailer, priceMode });
  const isLoading = requests?.[reqKey] === "loading";

  const productItems = useMemo(() => {
    if (isMultiRetailerCompareMode) {
      return (features || [])
        .filter(
          (p) => p?.title && Array.isArray(p?.comparison) && p.comparison.length > 0
        )
        .slice(0, 12);
    }
    if (isPlatformOfferMode) {
      const pool = (topDeals || []).filter((p) => p?.title);
      return pool
        .filter((p) => normalizeStoreKey(p?.platform) === retailer)
        .slice(0, 12);
    }
    const raw = byPlatform?.[retailer] || [];
    return (Array.isArray(raw) ? raw : []).filter((p) => p?.title).slice(0, 12);
  }, [isMultiRetailerCompareMode, isPlatformOfferMode, retailer, topDeals, features, byPlatform]);

  const cardPricingMode = isFeaturesOfferCompareMode || isPlatformOfferMode ? "Loyalty+Offer" : "Base";

  const handleSeeAll = () => {
    if (typeof onSeeAll === "function") {
      onSeeAll({ retailer, priceMode });
      return;
    }
    if (isFeaturesOfferCompareMode) {
      navigate("/features?offer=true");
      return;
    }
    if (isFeaturesCompareMode) {
      navigate("/features");
      return;
    }
    if (isPlatformOfferMode) {
      navigate(`/platform/${retailer}`);
      return;
    }
    if (RETAIL_KEYS.has(retailer)) navigate(`/platform/${retailer}`);
  };

  const openProduct = (product) => {
    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? "";
    if (!productId) return;
    if (typeof onOpenProduct === "function") {
      onOpenProduct(product, { retailer, priceMode });
      return;
    }
    if (isMultiRetailerCompareMode) {
      navigate(`/product/${encodeURIComponent(productId)}`, { state: { product } });
      return;
    }
    if (isPlatformOfferMode) {
      const platformKey = normalizeStoreKey(product?.platform) || retailer;
      navigate(
        `/product-store/${encodeURIComponent(platformKey)}/${encodeURIComponent(productId)}`,
        { state: { product } }
      );
      return;
    }
    if (RETAIL_KEYS.has(retailer)) {
      navigate(
        `/product-store/${encodeURIComponent(retailer)}/${encodeURIComponent(productId)}`,
        { state: { product } }
      );
    }
  };

  return (
    <section className="rounded-2xl bg-transparent px-3">
      <div className="flex flex-col gap-3 min-[400px]:flex-row min-[400px]:items-center min-[400px]:justify-between">
        <h2 className="font-semibold text-[#24303A] text-[20px]">{title}</h2>
        <button
          type="button"
          onClick={handleSeeAll}
          className="w-fit shrink-0 text-[18px] text-[#7352C7] sm:rounded-full sm:bg-[#7352C7] sm:px-8 sm:py-1.5 font-bold sm:text-white sm:shadow-sm"
        >
          See All
        </button>
      </div>

      <RetailerPriceModeFilters
        className="mt-3"
        retailer={retailer}
        priceMode={priceMode}
        onRetailerChange={setRetailer}
        onPriceModeChange={setPriceMode}
      />

      {isLoading && (
        <div className="mt-8 flex justify-center">
          <span className="text-[16px] text-[#6E7B86]">Loading…</span>
        </div>
      )}

      {!isLoading && productItems.length > 0 && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {productItems.map((product, idx) => {
              const id = product?.product_id ?? product?.pid ?? product?.sku ?? String(idx);
              const label = PLATFORM_LABELS[retailer] || "Store";
              return (
                <ProductCard
                  key={`${id}-${idx}`}
                  accent={["orange", "blue", "green", "purple"][idx % 4]}
                  product={product}
                  onAddToList={(product) =>
                    onAddToList(product, {
                      detailView: isMultiRetailerCompareMode ? "compare" : "single",
                      platform: isMultiRetailerCompareMode
                        ? undefined
                        : normalizeStoreKey(product?.platform) || retailer,
                    })
                  }
                  showComparison={isMultiRetailerCompareMode}
                  retailerLabel={isMultiRetailerCompareMode ? undefined : label}
                  pricingMode={cardPricingMode}
                  onClick={() => openProduct(product)}
                />
              );
            })}
          </div>
          <div className="mt-8 flex justify-center">
            <button type="button" onClick={handleSeeAll} className={SEE_ALL_BUTTON_CLASS}>
              See All
            </button>
          </div>
        </>
      )}

      {!isLoading && !productItems.length && (
        <p className="mt-6 text-center text-[16px] text-[#6E7B86]">
          No products to show for these filters.
        </p>
      )}
    </section>
  );
}
